import { Panel, Empty } from "./chartTheme";
import { PROVIDERS, canonicalProvider } from "../../lib/scorecard";
import { percentile } from "../../lib/reports";
import { testProviderLabel } from "../../lib/servers";
import { fmt } from "../../lib/api";
import { useSort, SortTh } from "../../lib/useSort";

const NETS = [...PROVIDERS, "Other"];
const med = (ts) => (ts?.length ? percentile(ts.map((t) => t.download_mbps), 50) : null);
const ACC = { server: (r) => r.server, All: (r) => med(r.all), ...Object.fromEntries(NETS.map((n) => [n, (r) => med(r.cells[n])])) };
const tone = (dl) => (dl >= 25 ? "bg-[#32D74B]/15 text-[#32D74B]" : dl >= 10 ? "bg-[#FF9F0A]/15 text-[#FF9F0A]" : "bg-[#FF3B30]/15 text-[#FF6B61]");

export const ServerComparison = ({ tests }) => {
  const by = {};
  tests.forEach((t) => {
    const srv = testProviderLabel(t);
    const net = canonicalProvider(t.isp) || "Other";
    ((by[srv] ||= {})[net] ||= []).push(t);
  });
  const servers = Object.keys(by).map((server) => ({ server, cells: by[server], all: Object.values(by[server]).flat() }))
    .sort((a, b) => b.all.length - a.all.length);
  const { sorted, sort, toggle } = useSort(servers, ACC);
  return (
    <Panel title="Server comparison: test provider & city × network" className="lg:col-span-3" testId="report-server-comparison">
      {!servers.length ? <Empty /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="server-comparison-table">
            <thead className="text-neutral-500 text-left">
              <tr>
                <SortTh k="server" label="Test server" sort={sort} toggle={toggle} />
                {NETS.map((n) => <SortTh key={n} k={n} label={n} sort={sort} toggle={toggle} />)}
                <SortTh k="All" label="All" sort={sort} toggle={toggle} />
              </tr>
            </thead>
            <tbody>
              {sorted.map(({ server: srv, all }) => {
                return (
                  <tr key={srv} className="border-t border-white/5" data-testid={`server-comparison-row-${srv}`}>
                    <td className="py-2 pr-3 text-white whitespace-nowrap">{srv}</td>
                    {[...NETS.map((n) => by[srv][n]), all].map((ts, i) => (
                      <td key={i} className="py-2 pr-3">
                        {ts?.length ? (
                          <div className={`px-2 py-1 ${tone(percentile(ts.map((t) => t.download_mbps), 50))}`}>
                            <div className="font-medium">{fmt(percentile(ts.map((t) => t.download_mbps), 50))}↓</div>
                            <div className="text-[10px] opacity-80">{fmt(percentile(ts.map((t) => t.ping_ms), 50), 0)}ms · n{ts.length}</div>
                          </div>
                        ) : <span className="text-neutral-700">—</span>}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[10px] text-neutral-600 mt-3">Each cell: median download (Mbps) · median ping · number of tests. Shows how each test server city (e.g. JNB, CPT, DUR) performs for every network.</div>
    </Panel>
  );
};
